import React, { useState, useEffect, useCallback, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import {
  __,
  formatDate,
  formatPrice,
  normalizeReferralCode,
} from '../functions/commom';
import { useParams, useSearchParams } from 'react-router-dom';
import { ReactComponent as LeftIcon } from '../assets/icons/left.svg';
import { ReactComponent as RightIcon } from '../assets/icons/right.svg';
import Button from './base/Button';
import CheckBox from './base/CheckBox';
import Turnstile from './base/Turnstile';
import moment from 'moment-timezone';
import {
  CURRENCY,
  DATE_FORMAT,
  IDENTITY_TYPE,
  SOURCE_OF_BUSINESS,
  SOURCE_OF_BUSINESS_OTHER,
} from '../config';
import Box from './Box';
import {
  InsuranceFormSubmission,
  InsuranceQuote,
} from '../types/insurance.types';
import { useAppState } from '../state/StateContext';
import Modal from './base/Modal';
import {
  getInsuranceCalculate,
  getSubmitInsurance,
} from '../functions/insuranceSearchService';

const useStyles = createUseStyles({
  noteContentWrapper: {
    position: 'relative',
  },
  noteContent: {
    fontSize: '14px',
    lineHeight: '1.6',
    maxHeight: '60vh', // Limit height for scrolling
    overflowY: 'auto',
    '& p': {
      marginBottom: '15px',
      marginTop: '0',
    },
    '& ul': {
      paddingLeft: '20px',
      marginBottom: '15px',
    },
    '& li': {
      marginBottom: '8px',
    },
    '& h4': {
      fontSize: '16px',
      fontWeight: 'bold',
      marginTop: '20px',
      marginBottom: '10px',
    },
  },
  scrollGradientBottom: {
    position: 'absolute',
    bottom: 0,
    left: 0,
    right: '10px', // Account for scrollbar
    height: '60px',
    background: 'linear-gradient(to top, rgba(255,255,255,1) 0%, rgba(255,255,255,0.8) 40%, rgba(255,255,255,0) 100%)',
    pointerEvents: 'none',
    zIndex: 1,
    opacity: 0,
    transition: 'opacity 0.2s ease',
  },
  scrollGradientBottomVisible: {
    opacity: 1,
  },
  formHeader: {
    borderBottom: '1px solid #ebebeb',
    paddingBottom: '30px',
    marginBottom: '30px',
  },
  formFooter: {
    display: 'flex',
    justifyContent: 'center',
    gap: '30px',
    marginTop: '20px',
  },
  formWrapper: {
    display: 'flex',
    flexDirection: 'column',
    gap: '20px',
  },
  formTitle: {
    fontSize: '24px',
    fontWeight: 'bold',
    borderLeft: '5px solid var(--dai-primary-color)',
    paddingLeft: '10px',
  },
  sectionTitle: {
    fontSize: '20px',
    fontWeight: 'bold',
  },
  marketingConsent: {
    marginBottom: '20px',
  },
  marketingConsentTitle: {
    fontSize: '16px',
    fontWeight: 'bold',
    marginBottom: '12px',
  },
  infoSection: {
    backgroundColor: '#f9f9f9',
    padding: '20px',
    borderRadius: '8px',
    marginBottom: '20px',
    '& .info-row': {
      display: 'flex',
      marginBottom: '10px',
      flexWrap: 'wrap',
      '& .info-label': {
        width: '200px',
        fontWeight: 'bold',
        color: '#555',
      },
      '& .info-value': {
        flex: 1,
      },
      '&:last-child': {
        marginBottom: '0',
      },
    },
  },
  premium: {
    fontSize: '24px',
    fontWeight: 'bold',
    color: 'var(--dai-primary-color)',
    textAlign: 'right',
    marginTop: '20px',
  },
  errorMessage: {
    color: 'red',
    padding: '10px',
    marginBottom: '20px',
    border: '1px solid red',
    borderRadius: '4px',
    backgroundColor: 'rgba(255, 0, 0, 0.05)',
  },
});

const InsuranceFormReview: React.FC = () => {
  const {
    id = 'single_trip',
    tier = 'basic',
    category = 'travel',
  } = useParams<{
    id?: string;
    tier?: string;
    category?: string;
  }>();

  const [searchParams] = useSearchParams();
  const classes = useStyles();
  const state = useAppState();

  const [insuranceQuoteResult, setInsuranceQuoteResult] =
    useState<InsuranceQuote | null>(null);

  const insurancePlans = state?.insurancePlans?.get();
  const selectedInsurance = Array.isArray(insurancePlans)
    ? insurancePlans.find((plan) => plan.id === id)
    : undefined;

  // For Travel
  const departureDate = searchParams.get('departureDate');
  const returnDate = searchParams.get('returnDate');
  const coverageType = searchParams?.get('coverageType');
  const withCruise = searchParams.get('withCruise');
  const couponCode = searchParams.get('couponCode');

  const withCruiseBoolean = withCruise === 'true';

  // For Home
  const buildingAge = searchParams.get('buildingAge');
  const floorArea = searchParams.get('floorArea');

  const days = selectedInsurance?.isAnnualPlan
    ? 365
    : moment(returnDate).diff(moment(departureDate), 'days') + 1;

  const selectedTier = insuranceQuoteResult
    ? insuranceQuoteResult?.quotes?.find((quote) => quote.id === tier)
    : null;

  // State for confirmation checkbox
  const [isConfirmed, setIsConfirmed] = useState(false);
  // Optional direct-marketing opt-in
  const [marketingConsent, setMarketingConsent] = useState(false);
  // ISO/UTC timestamp of when marketing consent was ticked (null if unticked)
  const [consentTimestamp, setConsentTimestamp] = useState<string | null>(null);
  const [errorMessage, setErrorMessage] = useState<string | undefined>(
    undefined
  );
  const [showNoteModal, setShowNoteModal] = useState(false);
  
  // State for Cloudflare Turnstile verification
  const [turnstileToken, setTurnstileToken] = useState<string | null>(null);
  const [turnstileError, setTurnstileError] = useState(false);
  // Disables Submit while a request is in flight. A ref is required as well:
  // setState has not re-rendered yet when a second click arrives in the same
  // turn, so the state check alone cannot stop a second POST.
  const [isSubmitting, setIsSubmitting] = useState(false);
  const submitInFlightRef = useRef(false);
  
  // Scroll indicator state for note modal (bottom gradient only)
  const [canScrollBottom, setCanScrollBottom] = useState(true);
  const noteContentRef = React.useRef<HTMLDivElement>(null);

  // Handle scroll in note modal to show/hide bottom gradient
  const handleNoteScroll = () => {
    if (noteContentRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = noteContentRef.current;
      setCanScrollBottom(scrollTop < scrollHeight - clientHeight - 10);
    }
  };

  // Check scroll state when modal opens
  useEffect(() => {
    if (showNoteModal && noteContentRef.current) {
      // Small delay to ensure content is rendered
      setTimeout(() => {
        handleNoteScroll();
      }, 100);
    }
  }, [showNoteModal]);

  // For Home
  const insuredPropertyRegion = state.form.insuredPropertyRegion.get();
  const insuredPropertyAddress = state.form.insuredPropertyAddress.get();

  // Insured persons
  const insuredPersons = state.form.insuredPersons.get() || [];

  // Helper function to get the source of business answer for display
  const getSourceOfBusinessLabel = () => {
    const value = state.form.sourceOfBusiness.get();
    if (value === SOURCE_OF_BUSINESS_OTHER) {
      return state.form.sourceOfBusinessOther.get() || '';
    }
    return __(SOURCE_OF_BUSINESS.find((option) => option.value === value)?.label);
  };

  // Helper function to get ID type label
  const getIdTypeLabel = (idType: string | null | undefined) => {
    if (!idType) return '';
    const idTypeOption = IDENTITY_TYPE.find(
      (option) => option.value === idType
    );
    return idTypeOption ? __(idTypeOption.label) : idType;
  };

  // Helper function to get gender label
  const getGenderLabel = (gender: string | null | undefined) => {
    if (!gender) return '';
    return gender === 'm' ? __('Male') : __('Female');
  };

  // Helper function to get relationship label
  const getRelationshipLabel = (relationship: string | null | undefined) => {
    if (!relationship) return '';

    switch (relationship) {
      case 'self':
        return __('Self');
      case 'spouse':
        return __('Spouse');
      case 'child':
        return __('Child');
      case 'parent':
        return __('Parent');
      default:
        return relationship;
    }
  };

  // Handle back button
  const handleBack = () => {
    state.formStep.set(category === 'travel' ? 2 : 1);
  };

  // Handle checkbox change
  const handleCheckboxChange = (checked: boolean) => {
    if (checked) {
      setShowNoteModal(true);
    } else {
      setIsConfirmed(false);
    }
  };

  // Handle modal confirm
  const handleModalConfirm = () => {
    setIsConfirmed(true);
    setShowNoteModal(false);
    setErrorMessage(undefined);
  };

  // Handle Turnstile verification
  const handleTurnstileVerify = useCallback((token: string) => {
    setTurnstileToken(token);
    setTurnstileError(false);
  }, []);

  const handleTurnstileError = useCallback(() => {
    setTurnstileToken(null);
    setTurnstileError(true);
    setErrorMessage(__('Verification failed. Please try again.'));
  }, []);

  const handleTurnstileExpire = useCallback(() => {
    setTurnstileToken(null);
    setErrorMessage(__('Verification expired. Please verify again.'));
  }, []);

  // Handle actual submit
  async function submitApplication() {
    if (submitInFlightRef.current) {
      return;
    }

    // Validate Turnstile token
    if (!turnstileToken) {
      setErrorMessage(__('Please complete the verification before submitting.'));
      return;
    }

    submitInFlightRef.current = true;
    setIsSubmitting(true);

    const params: InsuranceFormSubmission = {
      id: id,
      category: category,
      coverageType: coverageType || '',
      tier: tier,
      departureDate: departureDate || '',
      returnDate: returnDate || '',
      withCruise: withCruiseBoolean,
      couponCode: couponCode || '',
      referralCode: normalizeReferralCode(state?.form?.referralCode.get()),
      buildingAge: parseInt(buildingAge || '0'),
      floorArea: parseInt(floorArea || '0'),
      cfTurnstileToken: turnstileToken,
      directMarketingConsent: marketingConsent,
      consentTimestamp: consentTimestamp || undefined,
      policyHolder: {
        name: state?.form?.name.get() || '',
        gender: state?.form?.gender.get() || '',
        idType: state?.form?.idType.get() || '',
        id: state?.form?.id.get() || '',
        dateOfBirth: state?.form?.dateOfBirth.get() || '',
        phone: state?.form?.phone.get() || '',
        email: state?.form?.email.get() || '',
        sourceOfBusiness: state?.form?.sourceOfBusiness.get() || '',
        sourceOfBusinessOther: state?.form?.sourceOfBusinessOther.get() || '',
        address: state?.form?.address.get() || '',
        insuredPropertyRegion: state?.form?.insuredPropertyRegion.get() || '',
        insuredPropertyAddress: state?.form?.insuredPropertyAddress.get() || '',
      },
      insuredPersons: insuredPersons.map((person) => ({
        name: person.name || '',
        gender: person.gender || '',
        idType: person.idType || '',
        id: person.id || '',
        dateOfBirth: person.dateOfBirth || '',
        relationship: person.relationship || '',
      })),
    };

    console.log(params);

    let submitted = false;
    try {
      // submit to backend
      const response = await getSubmitInsurance(params);
      console.log(response);

      // Store the quote ID in state
      state.quoteId.set(response.quoteId);

      setErrorMessage(undefined);

      // Navigate to thank you page
      state.formStep.set(4);
      submitted = true;
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrorMessage(__('Failed to submit application. Please try again.'));
    } finally {
      // Release only after a failed request so the applicant can retry.
      // A successful submit leaves the review step mounted during the step
      // transition; clearing the lock there lets a second click POST again
      // and mint another TKS reference.
      if (!submitted) {
        submitInFlightRef.current = false;
        setIsSubmitting(false);
      }
    }
  }

  const planInfo = [
    {
      label: __('Plan Name'),
      value:
        id === 'single_trip'
          ? __(selectedInsurance?.name) + ' - ' + __(selectedTier?.name || '')
          : __(selectedInsurance?.name) + ' - ' + __(selectedTier?.name || ''),
    },
    {
      label: __('Coverage'),
      value: coverageType === 'individual' ? __('Individual') : __('Family'),
    },
    {
      label: selectedInsurance?.isAnnualPlan
        ? __('Effective Date')
        : __('Departure Date'),
      value: formatDate(departureDate),
    },
    {
      label: selectedInsurance?.isAnnualPlan
        ? __('End Date')
        : __('Return Date'),
      value: formatDate(returnDate),
    },
    { label: __('Duration'), value: `${days}${__('Days')}` },
    {
      label: __('With Cruise'),
      value: withCruiseBoolean ? __('Yes') : undefined,
    },
    {
      label: __('Currency'),
      value: CURRENCY,
    },
    {
      label: __('Premium'),
      value:
        formatPrice(
          selectedTier?.discount
            ? selectedTier?.discount
            : selectedTier?.premium || 0,
          '$'
        ) + (selectedInsurance?.isAnnualPlan ? '/' + __('Year') : ''),
    },
  ];

  const policyHolderInfo = [
    { label: __('Name (English)'), value: state.form.name.get() },
    {
      label: __('Date of Birth'),
      value: formatDate(state.form.dateOfBirth.get()),
    },
    { label: __('Gender'), value: getGenderLabel(state.form.gender.get()) },
    {
      label: __('Identity Type'),
      value: getIdTypeLabel(state.form.idType.get()),
    },
    { label: __('ID / Passport No.'), value: state.form.id.get() },
    {
      label: __('How did you hear about our travel insurance product?'),
      value: getSourceOfBusinessLabel(),
    },
    { label: __('Corresponding Address'), value: state.form.address.get() },
    { label: __('Phone'), value: state.form.phone.get() },
    { label: __('Email'), value: state.form.email.get() },
    {
      label: __('Referral Code'),
      value: normalizeReferralCode(state.form.referralCode.get()) || undefined,
    },
  ];

  const insuredProperty = [
    { label: __('Building Age'), value: buildingAge },
    { label: __('Floor Area'), value: floorArea },
    { label: __('Region'), value: __(insuredPropertyRegion) },
    { label: __('Property Address'), value: insuredPropertyAddress },
  ];

  useEffect(() => {
    const fetchInsuranceQuote = async () => {
      try {
        const result = await getInsuranceCalculate({
          id: id || 'single_trip',
          departureDate: moment(departureDate).format(DATE_FORMAT) || undefined,
          returnDate: moment(returnDate).format(DATE_FORMAT) || undefined,
          coverageType: coverageType || 'individual',
          category: category,
          withCruise: withCruiseBoolean,
          couponCode: couponCode || undefined,
          floorArea: parseInt(floorArea || '0'),
          buildingAge: parseInt(buildingAge || '0'),
        });

        setInsuranceQuoteResult(result);
      } catch (error) {
        console.error('Error fetching insurance quote:', error);
      }
    };

    fetchInsuranceQuote();
  }, [
    id,
    departureDate,
    returnDate,
    coverageType,
    category,
    withCruiseBoolean,
    floorArea,
    buildingAge,
    couponCode,
  ]);

  useEffect(() => {
    state.topbar.title.set(__('Review and Confirm'));
  }, [state.topbar.title]);

  return (
    <>
      <Box header={__('Review and Confirm')}>
        {errorMessage && (
          <div className={classes.errorMessage}>{errorMessage}</div>
        )}

        <div className={classes.formWrapper}>
          {/* Insurance Plan Information */}
          <div className={classes.sectionTitle}>{__('Insurance Plan')}</div>
          <div className={classes.infoSection}>
            {planInfo.map(
              (item) =>
                item.value && (
                  <div className="info-row" key={item.label}>
                    <div className="info-label">{item.label}</div>
                    <div className="info-value">{item.value}</div>
                  </div>
                )
            )}
          </div>

          {/* Policy Holder Information */}
          <div className={classes.sectionTitle}>
            {__('Policy Holder Information')}
          </div>
          <div className={classes.infoSection}>
            {policyHolderInfo.map(
              (item) =>
                item.value && (
                  <div className="info-row" key={item.label}>
                    <div className="info-label">{item.label}</div>
                    <div className="info-value">{item.value}</div>
                  </div>
                )
            )}
          </div>

          {/* Insured Persons Information */}
          {category === 'travel' ? (
            <>
              <div className={classes.sectionTitle}>
                {__('Insured Persons Information')}{' '}
                {__('(Total: $1)', insuredPersons.length)}
              </div>
              <div className="row">
                {insuredPersons.map((person, index) => (
                  <div className="col-md-12 col-xs-12" key={index}>
                    <div className={classes.infoSection}>
                      <div className="info-row">
                        <div className="info-label">
                          {__('Insured Person')} {index + 1}
                        </div>
                        <div className="info-value">
                          {getRelationshipLabel(person.relationship)}
                        </div>
                      </div>

                      <div className="info-row">
                        <div className="info-label">{__('Name (English)')}</div>
                        <div className="info-value">{person.name}</div>
                      </div>

                      <div className="info-row">
                        <div className="info-label">{__('Date of Birth')}</div>
                        <div className="info-value">
                          {formatDate(person.dateOfBirth)}
                        </div>
                      </div>

                      <div className="info-row">
                        <div className="info-label">{__('Gender')}</div>
                        <div className="info-value">
                          {getGenderLabel(person.gender)}
                        </div>
                      </div>

                      <div className="info-row">
                        <div className="info-label">{__('Identity Type')}</div>
                        <div className="info-value">
                          {getIdTypeLabel(person.idType)}
                        </div>
                      </div>

                      <div className="info-row">
                        <div className="info-label">
                          {__('ID / Passport No.')}
                        </div>
                        <div className="info-value">{person.id}</div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          ) : (
            <>
              <div className={classes.sectionTitle}>
                {__('Insured Property Information')}
              </div>
              <div className={classes.infoSection}>
                {insuredProperty.map(
                  (item) =>
                    item.value && (
                      <div className="info-row" key={item.label}>
                        <div className="info-label">{item.label}</div>
                        <div className="info-value">{item.value}</div>
                      </div>
                    )
                )}
              </div>
            </>
          )}

          {/* Direct marketing opt-in (optional) */}
          <div className={classes.marketingConsent}>
            <div className={classes.marketingConsentTitle}>
              {__(
                'Unlock Exclusive Privileges and Financial Insights from Delta Asia Financial Group'
              )}
            </div>
            <CheckBox
              label={__(
                'I would like to receive curated financial insights, preferential interest offers, and exclusive promotions from members of Delta Asia Insurance Limited and Banco Delta Asia S.A., and I consent to the use of my personal data provided herein for direct marketing purposes in accordance with the relevant provisions of the Personal Data Protection Act (Law No. 8/2005). I understand that I have the right to opt out of receiving such promotional information and withdraw this consent at any time.'
              )}
              checked={marketingConsent}
              onChange={(checked) => {
                setMarketingConsent(checked);
                // Record tick time in UTC; clear it if unticked
                setConsentTimestamp(checked ? new Date().toISOString() : null);
              }}
              id="marketingConsent"
            />
          </div>

          {/* Confirmation Checkbox */}
          <CheckBox
            label={__(
              'I confirm that all the information provided is correct and I agree to the terms and conditions of this insurance policy.'
            )}
            checked={isConfirmed}
            onChange={handleCheckboxChange}
            errorMessage={errorMessage}
            id="confirmInfo"
          />

          {/* Cloudflare Turnstile Verification */}
          {isConfirmed && (
            <div style={{ marginTop: '20px' }}>
              <Turnstile
                onVerify={handleTurnstileVerify}
                onError={handleTurnstileError}
                onExpire={handleTurnstileExpire}
                theme="light"
              />
              {turnstileError && (
                <p style={{ color: 'red', textAlign: 'center', marginTop: '10px' }}>
                  {__('Verification failed. Please refresh the page and try again.')}
                </p>
              )}
            </div>
          )}

          {/* Save to localStorage */}
          {/* <div style={{ marginTop: '20px' }}>
          <Button
            theme="secondary"
            onClick={saveFormDataToLocalStorage}
            style={{ marginRight: '10px' }}
          >
            {__('Save my information for next time')}
          </Button>
          {isSavedToLocalStorage && (
            <span style={{ color: 'green', marginLeft: '10px' }}>
              {__('Information saved successfully!')}
            </span>
          )}
          {hasSavedData && (
            <Button
              theme="secondary"
              onClick={clearSavedFormData}
              style={{ marginLeft: '10px' }}
            >
              {__('Clear saved information')}
            </Button>
          )}
        </div> */}

          {/* Form Footer */}
          <div className={classes.formFooter}>
            <Button
              onClick={handleBack}
              icon={<LeftIcon />}
              iconPosition="before"
            >
              {__('Back')}
            </Button>
            <div>
              <Button
                theme="primary"
                onClick={submitApplication}
                icon={<RightIcon />}
                iconPosition="after"
                disabled={!isConfirmed || !turnstileToken || isSubmitting}
              >
                {__('Submit')}
              </Button>
            </div>
          </div>
        </div>
      </Box>

      <Modal
        isOpen={showNoteModal}
        onClose={() => setShowNoteModal(false)}
        title={__('IMPORTANT_NOTES_TO_PROPOSER_TITLE')}
        footer={
          <div style={{ display: 'flex', gap: '10px' }}>
            <Button theme="secondary" onClick={() => setShowNoteModal(false)}>
              {__('Cancel')}
            </Button>
            <Button theme="primary" onClick={handleModalConfirm}>
              {__('I Agree')}
            </Button>
          </div>
        }
      >
        <div className={classes.noteContentWrapper}>
          <div 
            className={classes.noteContent}
            ref={noteContentRef}
            onScroll={handleNoteScroll}
          >
            <p>{__('IMPORTANT_NOTES_BODY_0')}</p>
            <p>{__('IMPORTANT_NOTES_BODY_1')}</p>
            <p>{__('IMPORTANT_NOTES_BODY_2')}</p>
            <p>{__('IMPORTANT_NOTES_BODY_3')}</p>
            <p>{__('IMPORTANT_NOTES_BODY_4')}</p>

            <h4>{__('IMPORTANT_NOTES_SECTION_2_TITLE')}</h4>
            <p>{__('IMPORTANT_NOTES_SECTION_2_BODY_1')}</p>
            <ul>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_1')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_2')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_3')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_4')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_5')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_6')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_7')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_8')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_9')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_10')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_11')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_12')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_13')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_ITEM_14')}</li>
            </ul>
            <p>{__('IMPORTANT_NOTES_SECTION_2_BODY_2')}</p>
            <p>{__('IMPORTANT_NOTES_SECTION_2_BODY_3')}</p>
            <ul>
              <li>{__('IMPORTANT_NOTES_LIST_2_ITEM_1')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_2_ITEM_2')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_2_ITEM_3')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_2_ITEM_4')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_2_ITEM_5')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_2_ITEM_6')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_2_ITEM_7')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_2_ITEM_8')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_2_ITEM_9')}</li>
              <li>{__('IMPORTANT_NOTES_LIST_2_ITEM_10')}</li>
            </ul>

            <h4>{__('IMPORTANT_NOTES_SECTION_3_TITLE')}</h4>
            <p>{__('IMPORTANT_NOTES_SECTION_3_BODY')}</p>
            
            <p style={{ marginTop: '20px', textAlign: 'center', fontSize: '12px', color: '#666' }}>
              {__('IMPORTANT_NOTES_COPYRIGHT')}
            </p>
          </div>
          
          {/* Bottom gradient overlay - shows when can scroll down */}
          <div 
            className={`${classes.scrollGradientBottom} ${canScrollBottom ? classes.scrollGradientBottomVisible : ''}`} 
          />
        </div>
      </Modal>
    </>
  );
};

export default InsuranceFormReview;
